import {
  AiReviewAgreementResponseSchema,
  AiReviewDisagreementsResponseSchema,
  type AiReviewAgreementResponse,
  type AiReviewDisagreementsResponse,
  AmountDistributionSchema,
  type CancelDisposition,
  AmountScatterSchema,
  ClearingPositionsResponseSchema,
  DashboardMetricsSchema,
  DepositorAbandonmentResponseSchema,
  DlqListResponseSchema,
  SettlementSchema,
  SettlementsListResponseSchema,
  SubmitSettlementSlipResponseSchema,
  MockScenarioSchema,
  OperatorAccountSchema,
  OperatorCancelWithdrawalResponseSchema,
  OperatorSubmitEvidenceResponseSchema,
  OperatorsListResponseSchema,
  ParticipantSchema,
  PayeeAccountSchema,
  ProviderQuotaResponseSchema,
  ReconHealthListResponseSchema,
  type ReconHealthRow,
  LedgerDivergenceListResponseSchema,
  type LedgerDivergenceRow,
  OpsLookupResponseSchema,
  type OpsLookupResponse,
  ReviewCaseContextSchema,
  ReviewQueueProjectionSchema,
  ParticipantsListResponseSchema,
  PolicyConfigSchema,
  ReviewCaseDetailSchema,
  RotateKeyResponseSchema,
  TransactionDetailSchema,
  type AmountDistribution,
  type AmountScatter,
  type ClearingPositionsResponse,
  type CreateParticipantRequest,
  type DashboardMetrics,
  type DepositStatus,
  type SettlementsListResponse,
  type MockScenario,
  type PayeeAccount,
  type PolicyConfig,
  type ProviderQuotaResponse,
  type ReviewCaseContext,
  type ReviewQueueProjection,
  type SigningKey,
  type UpdateParticipantRequest,
  TransactionHistoryResponseSchema,
  WithdrawalLineageResponseSchema,
  WithdrawalQueueResponseSchema,
  type TransactionHistoryResponse,
  type WithdrawalLineageResponse,
  type WithdrawalQueueResponse,
} from '@pf/contracts';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '@/lib/query-client';
import { useWebScope, webScopedKey } from '@/lib/scope/web-scope';
import { opsRequest, opsUrl } from './http';
import { ChangePasswordResponseSchema, type ChangePasswordRequest } from './ops-session';

/**
 * TanStack Query bindings for PaymentFlow's operator API.
 *
 * Every hook names the schema it validates against, so the type flowing into a component is
 * derived from `@pf/contracts` and cannot drift. Poll intervals are chosen per resource rather
 * than globally: the dashboard and the review queue move continuously and refresh on a timer;
 * participants and policy change only when an operator changes them, and refetch on demand.
 */

const SigningKeysSchema = z.object({ items: z.array(z.custom<SigningKey>()) }).strict();
const PayeeAccountsSchema = z.object({ items: z.array(PayeeAccountSchema) }).strict();

/**
 * Phase 3 clearing exposure (SAD §15) for the Clearing page. Polls like the dashboard: a net
 * position is a live solvency figure, not directory data.
 */
export function useClearingPositions(): UseQueryResult<ClearingPositionsResponse> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.clearing, participant),
    queryFn: ({ signal }) =>
      // "Involves", not "owes": an obligation names a debtor *and* a creditor, so a scoped view
      // keeps every pair the site is either side of. Anything narrower would net that site to a
      // different figure than the unscoped screen shows for it.
      opsRequest(ClearingPositionsResponseSchema, '/positions', { signal, query: { participant } }),
    refetchInterval: 15_000,
  });
}

/** Settlements, newest first (SAD §15, clearing M3). Polls with the positions it settles. */
export function useSettlements(): UseQueryResult<SettlementsListResponse> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.settlements, participant),
    queryFn: ({ signal }) =>
      // Scoped the same way the positions above are: either side of the transfer counts.
      opsRequest(SettlementsListResponseSchema, '/settlements', { signal, query: { participant } }),
    refetchInterval: 15_000,
  });
}

/**
 * MAKER freezes a pair's exposure into a payment order. 409 = the position moved; re-read.
 *
 * `amount` names what is actually being transferred when that is not the whole net (settling in
 * round money). Omitted means the whole position, netted server-side at the moment of the claim.
 */
export function useInstructSettlement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      debtor: string;
      creditor: string;
      currency: string;
      amount?: string;
    }) =>
      opsRequest(SettlementSchema, '/settlements', { method: 'POST', body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.ops.settlements });
      void client.invalidateQueries({ queryKey: queryKeys.ops.clearing });
    },
  });
}

/** CHECKER concludes an instructed settlement. FAILED releases the exposure back to positions. */
export function useDecideSettlement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; outcome: 'complete' | 'fail' }) =>
      opsRequest(SettlementSchema, `/settlements/${input.id}/${input.outcome}`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.ops.settlements });
      void client.invalidateQueries({ queryKey: queryKeys.ops.clearing });
    },
  });
}

/**
 * CHECKER attaches the transfer's bank slip (clearing M4). 202 — the provider's verdict lands
 * asynchronously, so the settlements list is what shows the outcome.
 */
export function useSubmitSettlementSlip() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; qrPayload: string }) =>
      opsRequest(SubmitSettlementSlipResponseSchema, `/settlements/${input.id}/slip`, {
        method: 'POST',
        body: { qr_payload: input.qrPayload },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.ops.settlements });
      void client.invalidateQueries({ queryKey: queryKeys.ops.clearing });
    },
  });
}

/**
 * The KPI window the Dashboard is asking for.
 *
 * Two shapes because `DashboardMetricsQuerySchema` accepts two, and refuses both at once: a
 * rolling `window_hours` for the presets, or an absolute `from`/`to` pair for a range an operator
 * typed. Modelling that as a discriminated union rather than as three loose optional fields means
 * "preset *and* custom" is not a state this console can get into and then have the API reject.
 *
 * `from`/`to` are already ISO-8601 with offset — the conversion out of the picker's local wall
 * clock happens once, where the operator's zone is known, not here.
 */
export type MetricsRange =
  | { readonly kind: 'preset'; readonly hours: number }
  | { readonly kind: 'custom'; readonly from: string; readonly to: string };

/** SAD §13 KPIs for the Dashboard over a chosen window. Polls every 15 s, as a live ops view should. */
export function useDashboardMetrics(range: MetricsRange): UseQueryResult<DashboardMetrics> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.metricsWindow(range), participant),
    queryFn: ({ signal }) =>
      opsRequest(DashboardMetricsSchema, '/metrics', {
        signal,
        // Exactly one of the two forms goes on the wire: the query schema is `.strict()` and
        // rejects a request that carries both.
        query:
          range.kind === 'preset'
            ? { window_hours: range.hours, participant }
            : { from: range.from, to: range.to, participant },
      }),
    refetchInterval: 15_000,
    // Switching windows keeps the previous window on screen while the new one loads, so the
    // dashboard never flashes back to a skeleton mid-shift.
    placeholderData: keepPreviousData,
  });
}

/**
 * The settled-amount histogram for the Dashboard, over the same window the KPIs use.
 *
 * A second call rather than another field on `/metrics` because it is a fundamentally different
 * aggregation — a per-transaction distribution, not a time series — and because the dashboard
 * must survive it failing: the KPIs above it stay on screen while the histogram renders its own
 * error. The window parameter is the same {@link MetricsRange} `useDashboardMetrics` takes, so the
 * two panels can never end up describing different spans.
 *
 * Every amount in the response is a decimal string. Nothing here parses one — the values travel
 * untouched to the component that renders them.
 */
export function useAmountDistribution(range: MetricsRange): UseQueryResult<AmountDistribution> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.amountDistribution(range), participant),
    queryFn: ({ signal }) =>
      opsRequest(AmountDistributionSchema, '/metrics/amount-distribution', {
        signal,
        // Exactly one of the two forms, for the same `.strict()` reason as the metrics query.
        query:
          range.kind === 'preset'
            ? { window_hours: range.hours, participant }
            : { from: range.from, to: range.to, participant },
      }),
    refetchInterval: 15_000,
    // Same reason as the KPIs: switching windows should re-shape the chart, not empty the page.
    placeholderData: keepPreviousData,
  });
}

/**
 * The settled deposits themselves, for the concentration-over-time scatter.
 *
 * A third call over the same window for the same reason the histogram is a second one: it is a
 * different question, it is the heaviest of the three payloads, and the panels above it have to
 * survive it failing. The response is *sampled* — `total_count` is the true population and
 * `sampled` says whether what arrived is all of it — so any caller that prints a count has to
 * print both numbers or it will overstate what is on screen.
 *
 * Polled on the same 15-second beat as its siblings. That is not free here (this is thousands of
 * points, not eight buckets), but a dashboard where one panel is a quarter of an hour older than
 * the one beside it is worse than a slightly larger poll: the whole value of the two charts is
 * that they describe the same deposits.
 *
 * Every amount stays a decimal string all the way to the chart. Nothing here parses one.
 */
export function useAmountScatter(range: MetricsRange): UseQueryResult<AmountScatter> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.amountScatter(range), participant),
    queryFn: ({ signal }) =>
      opsRequest(AmountScatterSchema, '/metrics/amount-scatter', {
        signal,
        // Exactly one of the two forms, for the same `.strict()` reason as the queries above.
        query:
          range.kind === 'preset'
            ? { window_hours: range.hours, participant }
            : { from: range.from, to: range.to, participant },
      }),
    refetchInterval: 15_000,
    // Same reason as the KPIs: switching windows should re-shape the chart, not empty the page.
    placeholderData: keepPreviousData,
  });
}

/** Filters the Review Queue page keeps in the URL. */
export interface ReviewQueueFilters {
  /** Case lifecycle status, or `ALL`. */
  status: string;
  /** Review reason, or `ALL`. */
  reason: string;
  /** `ALL`, `CLAIMED` or `UNCLAIMED`. */
  claimed: string;
  /** Minimum amount as a decimal string, or `''`. */
  minAmount: string;
  /** Exact amount as a decimal string, or `''` — ANDs with `minAmount` rather than replacing it. */
  amount: string;
  /** Free-text search: case/transaction id substring, or a whole username/account ref. */
  q: string;
}

/** The Review Queue projection (contract summary + read model). Polls every 20 s. */
export function useReviewQueue(filters: ReviewQueueFilters): UseQueryResult<ReviewQueueProjection> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.reviewQueue({ ...filters }), participant),
    queryFn: ({ signal }) =>
      opsRequest(ReviewQueueProjectionSchema, '/review-queue', {
        signal,
        query: {
          status: filters.status,
          reason: filters.reason,
          claimed: filters.claimed,
          min_amount: filters.minAmount,
          ...(filters.amount === '' ? {} : { amount: filters.amount }),
          q: filters.q,
          participant,
        },
      }),
    // New cases and claim/decision changes should surface promptly without a manual refresh; 5s
    // keeps the SLA-ordered queue feeling live while the operator works it.
    refetchInterval: 5_000,
  });
}

/**
 * One box, three buckets: "where is this customer's money right now" (`GET /ops/v1/lookup`).
 *
 * `enabled: q.trim().length > 0` rather than an empty-string 400 from the server — the endpoint's
 * own `OpsLookupQuerySchema` requires at least one character, and an empty box is this screen's
 * ordinary rest state, not an error condition to render. `keepPreviousData` matters more here than
 * on most lists: the box is retyped on every keystroke (after the URL's own debounce), and without
 * it every settled character would flash the result buckets back to a loading skeleton mid-scan.
 * Not polled — a lookup answers "right now, as of this click", and a stale answer an operator is
 * actively reading is safer than one that silently reshuffles under them while they act on it.
 */
export function useOpsLookup(filters: { q: string }): UseQueryResult<OpsLookupResponse> {
  const { participant } = useWebScope();
  const trimmed = filters.q.trim();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.lookup(trimmed), participant),
    queryFn: ({ signal }) =>
      opsRequest(OpsLookupResponseSchema, '/lookup', {
        signal,
        query: { q: trimmed, participant },
      }),
    enabled: trimmed.length > 0,
    placeholderData: keepPreviousData,
  });
}

/** Case statuses still in flight — a proposal, confirmation or claim can still change them. */
const LIVE_CASE_STATES = new Set(['OPEN', 'PROPOSED']);

/** A single review case, contract-exact. Polls while the case is still open, for dual control. */
export function useReviewCase(id: string) {
  return useQuery({
    queryKey: queryKeys.ops.reviewCase(id),
    queryFn: ({ signal }) => opsRequest(ReviewCaseDetailSchema, `/review-cases/${id}`, { signal }),
    enabled: id !== '',
    // So a checker sees the maker's proposal — and both see the resolution — without refreshing.
    // A resolved case is genuinely finished and is not polled again; when the operator still has
    // something to watch after it closes — a re-verification in flight — the live half is the
    // transaction, and {@link useOpsTransaction} polls that instead.
    refetchInterval: (query) =>
      LIVE_CASE_STATES.has(query.state.data?.status ?? '') ? 5_000 : false,
  });
}

/** The read-model context for a case: amount, SLA, claim, name score. Polls so claims show live. */
export function useReviewCaseContext(id: string): UseQueryResult<ReviewCaseContext> {
  return useQuery({
    queryKey: queryKeys.ops.reviewCaseContext(id),
    queryFn: ({ signal }) =>
      opsRequest(ReviewCaseContextSchema, `/review-cases/${id}/context`, { signal }),
    enabled: id !== '',
    refetchInterval: 5_000,
  });
}

/**
 * How often to re-read a transaction, per deposit state (SAD §4A).
 *
 * A total `Record<DepositStatus, …>` rather than a set of hand-written strings: the union comes
 * from `@pf/contracts`, so a tenth deposit state becomes a compile error here instead of a state
 * that silently means "poll forever". `false` is the stop.
 *
 * The 2 s beat belongs to the states a *worker* owns. "ตรวจสอบใหม่" hands the transaction back to
 * the verification worker, which picks it up within about a second, and the whole
 * VERIFYING → CONFIRMED → CREDIT_PENDING → COMPLETED run is over in a few more; at the page's
 * usual 5 s an operator would be watching a stale screen for most of it, and under 2 s the extra
 * requests buy nothing a human can perceive. The states that wait on a *person* — the depositor's
 * next slip, an operator's verdict — keep the 5 s beat the rest of this page already runs on.
 *
 * `EXPIRED` stops alongside the two genuinely terminal states even though a late slip can still
 * reopen it inside the grace window (see `DEPOSIT_STATE_META`): that reopening is a customer
 * uploading a slip minutes or hours later, not a worker seconds later, and it is not worth a
 * permanent background poll on every expired transaction somebody leaves open in a tab.
 */
const TRANSACTION_POLL_MS: Readonly<Record<DepositStatus, number | false>> = {
  RESERVED: 5_000,
  AWAITING_EVIDENCE: 5_000,
  VERIFYING: 2_000,
  UNDER_REVIEW: 5_000,
  CONFIRMED: 2_000,
  CREDIT_PENDING: 2_000,
  COMPLETED: false,
  EXPIRED: false,
  REJECTED: false,
};

/**
 * The transaction behind a case, read through the operator plane.
 *
 * Polls while the transaction is still moving, in the same conditional-interval shape as
 * {@link useReviewCase} above. This is what carries the manual-review screen across a
 * "ตรวจสอบใหม่": that action *closes* the case, so the case query stops polling by design — the
 * only thing still changing from then on is the transaction, and its status and event timeline are
 * what tell the operator whether the provider attested the slip, asked the depositor for another,
 * or sent the whole thing back under a brand-new review case. Without this the screen froze on the
 * pending state until somebody reloaded by hand.
 */
export function useOpsTransaction(id: string) {
  return useQuery({
    queryKey: queryKeys.ops.transaction(id),
    queryFn: ({ signal }) => opsRequest(TransactionDetailSchema, `/transactions/${id}`, { signal }),
    enabled: id !== '',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Nothing loaded yet: the first fetch is already in flight, and an interval before there is
      // a status to judge would be a poll with no stopping rule.
      return status === undefined ? false : TRANSACTION_POLL_MS[status];
    },
  });
}

/** The customer's raw uploaded slip file. `null` when none was uploaded (a QR-only submission). */
export interface EvidenceFile {
  /** A `data:` URI carrying the whole file — see {@link bytesToDataUrl} for why not a blob URL. */
  readonly url: string;
  readonly contentType: string;
}

/**
 * Render raw bytes as a `data:` URI rather than a `Blob` and `URL.createObjectURL`.
 *
 * A blob URL would be the natural choice — cheaper, and revocable — but jsdom (this console's test
 * environment) implements `Blob` without `arrayBuffer()` and does not implement
 * `URL.createObjectURL` at all, and a route that only renders in a real browser is not one a test
 * can prove works. Reading `response.arrayBuffer()` directly and base64-encoding it needs no
 * browser API beyond `btoa`, which every test and every browser has, at the cost of a ~33% larger
 * string — trivial next to the 5 MB `S3_MAX_OBJECT_BYTES` slip cap this is bounded by.
 */
function bytesToDataUrl(buffer: ArrayBuffer, contentType: string): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return `data:${contentType};base64,${btoa(binary)}`;
}

/**
 * `GET /ops/v1/transactions/{id}/evidence-file` — the customer's raw uploaded slip.
 *
 * Unlike every other hook here, this does not go through `opsRequest`: the response is bytes, not
 * a contract-shaped JSON body, so there is nothing to validate against a Zod schema. A plain
 * `fetch` with `credentials: 'include'` rides the same operator session cookie every other ops
 * call uses.
 *
 * A 404 (no uploaded file — SAD §6 allows a QR-only submission) and any other failure both resolve
 * to `null` rather than an error: the console's response is the same either way, "there is nothing
 * to show here", and `manual-review-page.tsx` hides the card entirely when this is `null`.
 */
export function useEvidenceFile(transactionId: string): UseQueryResult<EvidenceFile | null> {
  return useQuery({
    queryKey: queryKeys.ops.evidenceFile(transactionId),
    queryFn: async ({ signal }) => {
      const response = await fetch(opsUrl(`/transactions/${transactionId}/evidence-file`), {
        credentials: 'include',
        signal,
      });
      if (!response.ok) return null;
      const contentType = response.headers.get('content-type') ?? 'application/octet-stream';
      const buffer = await response.arrayBuffer();
      return { url: bytesToDataUrl(buffer, contentType), contentType };
    },
    enabled: transactionId !== '',
    // The file cannot change once submitted — no reason to poll or refetch on focus.
    staleTime: Infinity,
    retry: false,
  });
}

/** Invalidate everything a review decision could have changed. */
function useInvalidateCase(id: string): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.ops.reviewCase(id) }),
      client.invalidateQueries({ queryKey: queryKeys.ops.reviewCaseContext(id) }),
      client.invalidateQueries({ queryKey: ['ops', 'review-queue'] }),
      client.invalidateQueries({ queryKey: queryKeys.ops.metrics }),
      client.invalidateQueries({ queryKey: ['ops', 'transaction'] }),
    ]);
  };
}

/**
 * Propose a decision — the maker half of dual control.
 *
 * `EXTERNAL` is the third verdict: the admin already credited the depositor by hand, so the case
 * closes without a second credit while the withdrawer side settles normally. It rides the exact
 * propose/confirm path the other two do.
 */
export function useProposeDecision(caseId: string) {
  const invalidate = useInvalidateCase(caseId);
  return useMutation({
    mutationFn: (input: {
      decision: 'APPROVE' | 'REJECT' | 'EXTERNAL' | 'APPROVE_ADJUSTED';
      note: string;
    }) =>
      opsRequest(ReviewCaseDetailSchema, `/review-cases/${caseId}/propose`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

/** Confirm a proposed decision — the checker half. Refused if checker === maker. */
export function useConfirmDecision(caseId: string) {
  const invalidate = useInvalidateCase(caseId);
  return useMutation({
    mutationFn: () =>
      opsRequest(ReviewCaseDetailSchema, `/review-cases/${caseId}/confirm`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: invalidate,
  });
}

/**
 * Send a provider-outage case back through verification.
 *
 * `POST /ops/v1/review-cases/{id}/reverify` — the sibling of propose/confirm/claim, and the only
 * review action that decides nothing about the money: it re-submits the stored slip to the
 * provider and closes the case with the `REVERIFY` outcome. Whatever the provider answers then
 * drives the transaction exactly as a freshly submitted slip would, including opening a new case
 * if the answer is still inconclusive.
 *
 * Server-side permission is `review:reverify` (Maker, Checker, Super — the same set as
 * `review:propose`), which the console mirrors with `can('MAKER')`.
 *
 * The server answers `202`, not `200` (`routes/ops/review.ts`): re-verification only *starts*
 * here — the outcome arrives asynchronously on `slip.verified` / `transaction.confirmed` /
 * `transaction.rejected`. The body is the shared `ReviewCaseDetail` projection either way, and
 * `request()` cares only that the response is `ok`, so no status-specific handling is needed.
 * The request body is empty (`ReverifyReviewCaseRequestSchema` is `z.object({}).strict()`):
 * there is no judgment to record, so there is no note to send.
 */
export function useRequestReverify(caseId: string) {
  const invalidate = useInvalidateCase(caseId);
  return useMutation({
    mutationFn: () =>
      opsRequest(ReviewCaseDetailSchema, `/review-cases/${caseId}/reverify`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: invalidate,
  });
}

/** Claim or release a case, so two operators do not work the same one. */
export function useCaseClaim(caseId: string) {
  const invalidate = useInvalidateCase(caseId);
  return useMutation({
    mutationFn: (input: { claim: boolean }) =>
      opsRequest(ReviewCaseDetailSchema, `/review-cases/${caseId}/claim`, {
        method: input.claim ? 'POST' : 'DELETE',
      }),
    onSuccess: invalidate,
  });
}

/** Registered participants (M-07 Directory). */
export function useParticipants() {
  return useQuery({
    queryKey: queryKeys.ops.participants,
    queryFn: ({ signal }) =>
      opsRequest(ParticipantsListResponseSchema, '/participants', { signal }),
  });
}

/** A participant's signing keys, including the overlapping retiring one (SAD §11). */
export function useSigningKeys(participantId: string) {
  return useQuery({
    queryKey: queryKeys.ops.keys(participantId),
    queryFn: ({ signal }) =>
      opsRequest(SigningKeysSchema, `/participants/${participantId}/keys`, { signal }),
    enabled: participantId !== '',
  });
}

/** Register a participant. Admin only. */
export function useCreateParticipant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateParticipantRequest) =>
      opsRequest(ParticipantSchema, '/participants', { method: 'POST', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.participants }),
  });
}

/**
 * Suspend/reactivate a participant, rename it, or move its clearing pool. `clearing_group: null`
 * moves the website back to a pool of one; omitting the key (not just passing a falsy one) leaves
 * the pool untouched — `UpdateParticipantRequest` keeps the two distinct on the wire, so callers
 * must not default the field to `null` themselves. Admin only.
 */
export function useUpdateParticipant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & UpdateParticipantRequest) => {
      const { id, ...body } = input;
      return opsRequest(ParticipantSchema, `/participants/${id}`, { method: 'PATCH', body });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.participants }),
  });
}

/** The console operator roster. Admin only — the API 403s every other role. */
export function useOperators() {
  return useQuery({
    queryKey: queryKeys.ops.operators,
    queryFn: ({ signal }) => opsRequest(OperatorsListResponseSchema, '/operators', { signal }),
  });
}

/** Create an operator account. Admin only. */
export function useCreateOperator() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      username: string;
      display_name: string;
      role: 'VIEWER' | 'MAKER' | 'CHECKER' | 'ADMIN' | 'SUPER';
      password: string;
    }) => opsRequest(OperatorAccountSchema, '/operators', { method: 'POST', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.operators }),
  });
}

/** Rename, change role, suspend/reactivate, or reset the password of an operator. Admin only. */
export function useUpdateOperator() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      display_name?: string;
      role?: 'VIEWER' | 'MAKER' | 'CHECKER' | 'ADMIN' | 'SUPER';
      status?: 'ACTIVE' | 'SUSPENDED';
      password?: string;
    }) => {
      const { id, ...body } = input;
      return opsRequest(OperatorAccountSchema, `/operators/${id}`, { method: 'PATCH', body });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.operators }),
  });
}

/**
 * Change your own password. Every role; the current password is fresh proof, and a wrong one is
 * a 403 that leaves the session intact (the API's contract — see `currentPasswordIncorrect`).
 * On success the server revokes every *other* session this operator holds.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordRequest) =>
      opsRequest(ChangePasswordResponseSchema, '/session/password', {
        method: 'POST',
        body: input,
      }),
  });
}

/** Rotate a signing key. The secret comes back exactly once (SAD §11). */
export function useRotateKey(participantId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      opsRequest(RotateKeyResponseSchema, `/participants/${participantId}/keys`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.keys(participantId) }),
  });
}

/** Revoke a signing key. Refused when it is the only active key. */
export function useRevokeKey(participantId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      opsRequest(
        z.object({ key_id: z.string(), status: z.literal('REVOKED') }).strict(),
        `/participants/${participantId}/keys/${keyId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.keys(participantId) }),
  });
}

/** Registered payee accounts and their name-matching aliases. */
export function usePayeeAccounts(): UseQueryResult<{ items: PayeeAccount[] }> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.payeeAccounts, participant),
    queryFn: ({ signal }) =>
      opsRequest(PayeeAccountsSchema, '/payee-accounts', { signal, query: { participant } }),
  });
}

/**
 * The webhook dead-letter queue (SAD §9), for the console's current website scope.
 *
 * This endpoint has had a `participant` filter since it shipped, driven by a picker of its own on
 * the Admin screen. That picker is gone: it sent the literal string `ALL` on the wire for its
 * "all participants" option, which the API reads as a participant *named* `ALL` and answers with
 * an empty queue — so the tab's default view was, against a real server, silently showing nothing
 * rather than everything. The console-wide scope has no such sentinel to leak: the whole pool is
 * `undefined`, and `buildUrl` drops it.
 */
export function useDlq() {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.dlq, participant),
    queryFn: ({ signal }) =>
      opsRequest(DlqListResponseSchema, '/dlq', { signal, query: { participant } }),
    refetchInterval: 30_000,
  });
}

/** Replay a dead-lettered delivery. */
export function useReplayDelivery() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      opsRequest(
        z.object({ delivery_id: z.string(), status: z.literal('REPLAY_QUEUED') }).strict(),
        `/dlq/${deliveryId}/replay`,
        { method: 'POST', body: {} },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['ops', 'dlq'] });
      await client.invalidateQueries({ queryKey: queryKeys.ops.metrics });
    },
  });
}

/**
 * Recon-feed polling health per website (2026-08-27 observability pair, `@pf/contracts`
 * `observability.ts`). A website can report up to two rows — one per feed (`transactions`,
 * `withdrawals`) — and it is the dashboard's job to fold those into one card per website; this
 * hook just hands back whatever rows the scope matches.
 *
 * Polled at the same cadence as {@link useDlq}: a feed that stops polling is exactly the kind of
 * thing an operator should see within half a minute of it happening, not on the next full reload.
 */
export function useReconHealth(): UseQueryResult<{ items: ReconHealthRow[] }> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(queryKeys.ops.reconHealth, participant),
    queryFn: ({ signal }) =>
      opsRequest(ReconHealthListResponseSchema, '/recon-health', { signal, query: { participant } }),
    refetchInterval: 30_000,
  });
}

/** Filters `GET /ops/v1/ledger-divergences` takes — every one optional, mirroring the endpoint. */
export interface LedgerDivergencesFilters {
  /** Only rows PF received at/after this instant. Omit for no lower bound. */
  since?: string;
  /** Max rows, newest first (server default 50, ceiling 200). */
  limit?: number;
}

/**
 * Ledger divergences a participant's own reconciliation detected — the intake half of the same
 * observability pair as {@link useReconHealth}. One hook, two callers: the dashboard's 24h
 * stuck-count tile (`since` set to the rolling window, `limit` at the server's ceiling so the
 * count is not itself truncated by the default page size) and the full list page (`since`
 * omitted, `limit: 200`). Both want the same wire shape and the same website scoping, so a second
 * hook would only be a second place for the two to drift apart.
 */
export function useLedgerDivergences(
  filters: LedgerDivergencesFilters = {},
): UseQueryResult<{ items: LedgerDivergenceRow[] }> {
  const { participant } = useWebScope();
  const { since, limit } = filters;
  return useQuery({
    queryKey: webScopedKey(
      queryKeys.ops.ledgerDivergences({
        since: since ?? '',
        limit: limit === undefined ? '' : String(limit),
      }),
      participant,
    ),
    queryFn: ({ signal }) =>
      opsRequest(LedgerDivergenceListResponseSchema, '/ledger-divergences', {
        signal,
        query: { participant, since, limit },
      }),
    refetchInterval: 30_000,
  });
}

/** Policy tunables (SAD §16 Q1–Q8). */
export function usePolicy(): UseQueryResult<PolicyConfig> {
  return useQuery({
    queryKey: queryKeys.ops.policy,
    queryFn: ({ signal }) => opsRequest(PolicyConfigSchema, '/policy', { signal }),
  });
}

/** Update policy tunables. Admin only; validated server-side too. */
export function useUpdatePolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<PolicyConfig>) =>
      opsRequest(PolicyConfigSchema, '/policy', { method: 'PUT', body: patch }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.policy }),
  });
}

/**
 * Slip-verification quota, per provider (Thunder, Slip2Go, …), across the whole platform.
 *
 * Deliberately *not* scoped the way its neighbours on the dashboard are: every other hook in this
 * file that reads console-wide data calls `useWebScope()` and sends `participant`, but PaymentFlow
 * holds one vendor account per provider — not one per website — so there is no per-participant
 * partition to narrow this to. Sending `participant` here would either be silently ignored by the
 * server or, worse, produce an answer that reads as "this website's quota" when no such thing
 * exists. Modelled on {@link usePolicy} — the other system-wide read on this page — rather than on
 * `useDashboardMetrics` and its siblings.
 *
 * `refetchInterval: 60_000` matches the server's own cache TTL for this figure (it calls out to
 * each vendor to build it): polling faster would only spend requests re-asking a question whose
 * answer has not moved.
 */
export function useProviderQuota(): UseQueryResult<ProviderQuotaResponse> {
  return useQuery({
    queryKey: queryKeys.ops.providerQuota,
    queryFn: ({ signal }) =>
      opsRequest(ProviderQuotaResponseSchema, '/provider-quota', { signal }),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

const ScenarioSchema = z.object({ scenario: MockScenarioSchema }).strict();

/** The slip-verification scenario the mock provider is pinned to. */
export function useMockScenario() {
  return useQuery({
    queryKey: queryKeys.ops.scenario,
    queryFn: ({ signal }) => opsRequest(ScenarioSchema, '/mock/scenario', { signal }),
  });
}

/** Pin the mock provider to a scenario, driving the SAD §6 decision matrix from the UI. */
export function useSetMockScenario() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (scenario: MockScenario) =>
      opsRequest(ScenarioSchema, '/mock/scenario', { method: 'PUT', body: { scenario } }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.ops.scenario }),
  });
}

/** Filters the Withdrawal queue page keeps in state. */
export interface WithdrawalQueueFilters {
  /** `WAITING`, `IN_FLIGHT` or `DONE`. */
  group: string;
  /** One wire status within the group, or `ALL`. */
  status: string;
  /** Free-text search over id, participant, payee name and account number. */
  q: string;
  /** Exact amount as a decimal string, or `''` — satang-precise, unlike `q`. */
  amount: string;
  /** `CREATED` (time in pool) or `AMOUNT`. */
  sort: string;
  /** `ASC` or `DESC`. */
  dir: string;
  /** Zero-based page, `limit`-sized. */
  page: number;
  /** Rows per page. */
  limit: number;
}

/** The withdrawal pool, grouped. Polls at dashboard cadence — it is a monitoring surface. */
export function useWithdrawalQueue(
  filters: WithdrawalQueueFilters,
): UseQueryResult<WithdrawalQueueResponse> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(
      queryKeys.ops.withdrawalQueue({
        group: filters.group,
        status: filters.status,
        q: filters.q,
        amount: filters.amount,
        sort: filters.sort,
        dir: filters.dir,
        page: String(filters.page),
        limit: String(filters.limit),
      }),
      participant,
    ),
    queryFn: ({ signal }) =>
      opsRequest(WithdrawalQueueResponseSchema, '/withdrawals', {
        signal,
        query: {
          group: filters.group,
          status: filters.status,
          ...(filters.q === '' ? {} : { q: filters.q }),
          ...(filters.amount === '' ? {} : { amount: filters.amount }),
          sort: filters.sort,
          dir: filters.dir,
          limit: String(filters.limit),
          offset: String(filters.page * filters.limit),
          participant,
        },
      }),
    refetchInterval: 15_000,
    // Tab, status, search, sort and page are all part of the key, so without this every one of
    // them empties the cache slot and drops the operator back to a skeleton — mid-scan, several
    // times per search. Holding the previous page until the new one lands makes the queue read
    // as being filtered rather than reloaded.
    placeholderData: keepPreviousData,
  });
}

/** Default rows per Withdrawal-queue page; the pager offers the other sizes. */
export const WITHDRAWALS_PAGE_SIZE = 50;

/**
 * Cancel a waiting or unpaid-reserved withdrawal on the customer's behalf (MAKER or CHECKER,
 * mandatory reason).
 *
 * Legal while the row is AVAILABLE or RESERVED — one that got matched and paid in the meantime
 * 409s. The invalidation runs on settle, not just success, because after a conflict the row the
 * operator is looking at is stale by definition and the queue must re-fetch to show its real
 * state.
 *
 * `disposition` says what the *website* should do with the released money: omitted (or `RELEASE`)
 * leaves it in the customer's wallet, `MOVE_TO_NORMAL` asks the website to re-file it as an
 * ordinary withdrawal in its own payout queue. Either way this cancel is the same cancel here —
 * PF releases the lock and the row lands CANCELLED; the value only rides the outgoing
 * `withdrawal.released` event to the website that owns that queue.
 */
export function useCancelQueuedWithdrawal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string; disposition?: CancelDisposition }) =>
      opsRequest(OperatorCancelWithdrawalResponseSchema, `/withdrawals/${input.id}/cancel`, {
        method: 'POST',
        body:
          input.disposition === undefined
            ? { reason: input.reason }
            : { reason: input.reason, disposition: input.disposition },
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['ops', 'withdrawal-queue'] });
      void client.invalidateQueries({ queryKey: queryKeys.ops.metrics });
    },
  });
}

/**
 * Split-fill lineage for one withdrawal (migration 0034 / shrink-the-parent): how many slices
 * were cut and for how much, which deposits filled them, how many were paid, which failed and
 * why, and whether a funds lock was released back. `id` may be a parent's or a slice's — the
 * server resolves to the root either way, which is why the History detail page can pass
 * `matched_withdrawal_id` straight through without knowing which kind of id it holds.
 *
 * `null` disables the query rather than the caller having to guard the call site: the History
 * detail page's `matched_withdrawal_id` is legitimately `null` for most deposits (nothing to do
 * with a split), and that is a fact about the transaction, not a loading state.
 *
 * Not a live-polling surface — a lineage does not move on its own the way the withdrawal queue
 * does, so there is no `refetchInterval` here. And a 404 is not a failure worth retrying: most
 * withdrawals were never split, so "no lineage" is the ordinary answer for almost every id this
 * is asked about. The shared retry policy in `query-client.ts` already leaves a non-retryable 404
 * alone, so nothing extra is needed here either.
 */
export function useWithdrawalLineage(
  id: string | null,
): UseQueryResult<WithdrawalLineageResponse> {
  return useQuery({
    queryKey: queryKeys.ops.withdrawalLineage(id ?? ''),
    queryFn: ({ signal }) =>
      opsRequest(WithdrawalLineageResponseSchema, `/withdrawals/${id ?? ''}/lineage`, { signal }),
    enabled: id !== null,
  });
}

/** The slip file types the assisted upload accepts — the contract's `content_type` enum. */
export const SLIP_CONTENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const;

/** The storage layer's object cap (`S3_MAX_OBJECT_BYTES`); larger files are refused client-side. */
export const SLIP_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Read a `File` as standard base64 (no data-URI prefix).
 *
 * `FileReader.readAsDataURL` rather than `file.arrayBuffer()` for the same reason
 * {@link bytesToDataUrl} avoids blob URLs: jsdom's `Blob` has no `arrayBuffer()`, and an upload
 * path only a real browser can execute is one no test can prove works.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(reader.error ?? new Error('Could not read the file.'));
    };
    reader.onload = () => {
      // `readAsDataURL` always yields a string; the `string | ArrayBuffer | null` union exists
      // because one `FileReader` type covers `readAsArrayBuffer` too. Narrowed rather than
      // `String()`-coerced: coercion would turn the impossible branch into the literal text
      // "[object ArrayBuffer]" and upload *that* as the slip.
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read the file.'));
        return;
      }
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * File a slip on the customer's behalf (`POST /ops/v1/transactions/{id}/evidence`).
 *
 * The assisted path for a customer who cannot upload the slip themselves — support receives the
 * image out-of-band and the operator submits it from the withdrawal queue. 202: verification is
 * asynchronous, so the queue (RESERVED → LOCKED) is what shows the outcome. Invalidation runs on
 * settle for the same reason the cancel's does: after a 409 the row on screen is stale by
 * definition.
 */
export function useOperatorSubmitEvidence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { transactionId: string; file: File }) =>
      opsRequest(
        OperatorSubmitEvidenceResponseSchema,
        `/transactions/${input.transactionId}/evidence`,
        {
          method: 'POST',
          body: {
            kind: 'BANK_SLIP',
            content_type: input.file.type,
            file_base64: await fileToBase64(input.file),
          },
        },
      ),
    onSettled: (_data, _error, input) => {
      void client.invalidateQueries({ queryKey: ['ops', 'withdrawal-queue'] });
      void client.invalidateQueries({ queryKey: queryKeys.ops.metrics });
      void client.invalidateQueries({
        queryKey: queryKeys.ops.transaction(input.transactionId),
      });
      void client.invalidateQueries({
        queryKey: queryKeys.ops.evidenceFile(input.transactionId),
      });
    },
  });
}

/** Filters the History page keeps in state. */
export interface TransactionHistoryFilters {
  /** `ALL` or one terminal status. */
  status: string;
  /**
   * A transaction-id substring, a depositor's whole `account_ref`, or `''`.
   *
   * The two are matched differently on purpose — substring for the id, whole value for the ref —
   * because a ref is an opaque customer number that only means anything complete. The server is
   * the authority on that rule; see `transactionHistory` in the metrics repository.
   */
  search: string;
  /** Exact amount as a decimal string, or `''` — satang-precise, unlike `search`. */
  amount: string;
  /** `FINISHED` (terminal-state time) or `AMOUNT`. */
  sort: string;
  /** `ASC` or `DESC`. */
  dir: string;
  /** Zero-based page, `limit`-sized. */
  page: number;
  /** Rows per page. */
  limit: number;
}

/** Finished deposits, newest first. No polling — history does not move. */
export function useTransactionHistory(
  filters: TransactionHistoryFilters,
): UseQueryResult<TransactionHistoryResponse> {
  const { participant } = useWebScope();
  return useQuery({
    queryKey: webScopedKey(
      queryKeys.ops.transactionHistory({
        status: filters.status,
        search: filters.search,
        amount: filters.amount,
        sort: filters.sort,
        dir: filters.dir,
        page: String(filters.page),
        limit: String(filters.limit),
      }),
      participant,
    ),
    queryFn: ({ signal }) =>
      opsRequest(TransactionHistoryResponseSchema, '/transactions', {
        signal,
        query: {
          status: filters.status,
          // A depositor's `account_ref` is unique per participant, not globally, so an unscoped
          // search for one can legitimately return two sites' rows. Scoping the console is what
          // makes that search mean one customer.
          ...(filters.search === '' ? {} : { search: filters.search }),
          ...(filters.amount === '' ? {} : { amount: filters.amount }),
          sort: filters.sort,
          dir: filters.dir,
          limit: String(filters.limit),
          offset: String(filters.page * filters.limit),
          participant,
        },
      }),
  });
}

/** Default rows per History page; the pager offers the other sizes. */
export const HISTORY_PAGE_SIZE = 50;

/**
 * The abandonment report, plus the one field the shipped contract does not carry yet.
 *
 * ## Why this extension exists
 *
 * `cancelled` and `timed_out` are only distinguishable for events recorded *after* the domain
 * change that started stamping `transaction.expired` with a `cause`. Before it, both behaviours
 * wrote the identical event and every historical deliberate release is permanently filed as a
 * timeout. It cannot be back-filled: the signal was never recorded.
 *
 * That is not a cosmetic caveat on this screen. `timed_out` is the column rendered as the *worse*
 * behaviour, and it is exactly the column inflated for any window reaching back past the cutover —
 * on a screen whose output is a decision to restrict or block a real person. So the report has to
 * say, per window, from when the split can be trusted, and `split_reliable_from` is that instant.
 *
 * ## Why it is declared here rather than waited for
 *
 * The field is agreed and being added to `DepositorAbandonmentResponseSchema`; it had not landed
 * when this screen was wired. Rather than guess a shape inline at the call site, the extension is
 * one declaration: `.extend()` on the contract schema, so *every other field stays the contract's*
 * and only this one is local. It is `.optional()` as well as `.nullable()` on purpose — that is
 * what makes the same parse correct before the field ships (absent), after it ships with no
 * cutover recorded (`null`, e.g. a fresh deployment), and after it ships with one (an instant).
 *
 * When the contract lands the field, this whole block deletes and the schema import is used
 * directly; nothing else on the screen changes, because everything already reads it from here.
 */
export const AbandonmentReportSchema = DepositorAbandonmentResponseSchema.extend({
  /**
   * The instant from which `cancelled` / `timed_out` are trustworthy, or `null` when nothing in
   * the data predates the split. A window starting before this carries misattributed timeouts.
   */
  split_reliable_from: z.string().datetime({ offset: true }).nullable().optional(),
});

/** The abandonment report as this console consumes it. See {@link AbandonmentReportSchema}. */
export type AbandonmentReport = z.infer<typeof AbandonmentReportSchema>;

/** Filters the depositor-abandonment leaderboard keeps in the URL. */
export interface DepositorAbandonmentFilters {
  /**
   * The window being reported on — the same {@link MetricsRange} the two dashboard panels take.
   *
   * The one type rather than a bare hour count, because `/depositors/abandonment` shares
   * `resolveRange` with `/metrics` on the server and therefore accepts exactly the same two forms.
   * Modelling it as the same union here is what lets an operator carry a window between the two
   * screens without either of them having to translate it — and, as on the dashboard, makes
   * "preset *and* custom" a state this console cannot get into and then have the API reject.
   */
  range: MetricsRange;
  /** Hide accounts with fewer reservations than this, so the one-off tail can be excluded. */
  minReservations: number;
  /** One of `DEPOSITOR_ABANDONMENT_SORTS`. */
  sort: string;
  /** `ASC` or `DESC`. */
  dir: string;
  /** Zero-based page, `limit`-sized. */
  page: number;
  /** Rows per page. */
  limit: number;
}

/**
 * Who reserves a slot and then does not follow through, worst first.
 *
 * Polled, unlike History, and not polled at dashboard cadence either. A depositor's behaviour is
 * not a rear-view mirror — an account that starts holding slots hostage should climb this table
 * while the operator is still looking at it — but neither does the Wilson ordering of a 24-hour
 * window visibly move every fifteen seconds. A minute is the honest cadence for the question, and
 * it keeps a screen someone leaves open all shift from being the console's noisiest caller.
 */
export function useDepositorAbandonment(
  filters: DepositorAbandonmentFilters,
): UseQueryResult<AbandonmentReport> {
  const { participant } = useWebScope();
  // Exactly one of the two forms, for the same `.strict()` reason the metrics queries give: the
  // endpoint's schema refuses a request carrying both. Built once and spread into the key *and*
  // the query string, so the thing cached and the thing requested cannot describe different
  // windows — and so a preset and a custom range of the same length never collide in the cache.
  const window =
    filters.range.kind === 'preset'
      ? { window_hours: String(filters.range.hours) }
      : { from: filters.range.from, to: filters.range.to };
  return useQuery({
    queryKey: webScopedKey(
      queryKeys.ops.depositorAbandonment({
        ...window,
        min_reservations: String(filters.minReservations),
        sort: filters.sort,
        dir: filters.dir,
        page: String(filters.page),
        limit: String(filters.limit),
      }),
      participant,
    ),
    queryFn: ({ signal }) =>
      opsRequest(AbandonmentReportSchema, '/depositors/abandonment', {
        signal,
        query: {
          ...window,
          min_reservations: String(filters.minReservations),
          sort: filters.sort,
          dir: filters.dir,
          limit: String(filters.limit),
          offset: String(filters.page * filters.limit),
          participant,
        },
      }),
    refetchInterval: 60_000,
    // Every filter is part of the key, so without this each change of window, threshold, sort or
    // page empties the cache slot and drops the operator back to a skeleton mid-scan. Holding the
    // previous rows until the new ones land makes the table read as being re-ordered rather than
    // reloaded — which matters most on this screen, because re-ordering is what it is *for*.
    placeholderData: keepPreviousData,
  });
}

/**
 * Default rows per leaderboard page.
 *
 * Smaller than History's 50 because the two are read differently: History is scanned for one row
 * an operator already knows exists, whereas a leaderboard is read from the top and acted on. The
 * accounts worth acting on are in the first screenful; a longer default page just buys scrolling.
 */
export const ABANDONMENT_PAGE_SIZE = 25;

/**
 * Per-reason agreement between the shadow AI reviewer and the operators who actually decided each
 * case, over a window. The API never sends a platform total (see `AiReviewAgreementResponseSchema`
 * in `@pf/contracts`) — `rows` is the whole answer, one row per review reason.
 *
 * Modelled on {@link usePolicy} above rather than on `useDashboardMetrics` and its siblings: this
 * is a platform-wide report about PaymentFlow's own reviewers, with no per-participant partition,
 * so it does not call `useWebScope()` and never sends `participant` on the wire.
 */
/**
 * One reviewer to narrow the agreement report to — a `(model, prompt_version)` pair, never a
 * model alone.
 *
 * The pair is the unit because `ai_review_opinion` is keyed on all three of case, model and
 * prompt version: a prompt edit changes what the model was asked, so two prompt versions of one
 * model are two reviewers whose rates must not be averaged together.
 */
export interface AiReviewReviewerSelection {
  readonly model: string;
  readonly promptVersion: string;
}

/**
 * Per-review-reason agreement rates between the shadow reviewer and the humans who decided the
 * same cases, over `range`.
 *
 * `reviewer` narrows to one `(model, prompt_version)` pair; `null` reads the whole window, and
 * the response then says which reviewers answered in it so a picker can be built from the data
 * rather than from a hardcoded list.
 */
export function useAiReviewAgreement(
  range: MetricsRange,
  reviewer: AiReviewReviewerSelection | null = null,
): UseQueryResult<AiReviewAgreementResponse> {
  return useQuery({
    queryKey: queryKeys.ops.aiReviewAgreement(range, reviewer),
    queryFn: ({ signal }) =>
      opsRequest(AiReviewAgreementResponseSchema, '/ai-review/agreement', {
        signal,
        // Exactly one of the two forms, for the same `.strict()` reason the metrics queries give.
        query: {
          ...(range.kind === 'preset'
            ? { window_hours: range.hours }
            : { from: range.from, to: range.to }),
          // Both halves or neither. The wire takes them as independent optional filters, but a
          // model without its prompt version is not a reviewer — sending one alone would silently
          // blend two prompts under one model back into a single rate, which is the exact mixing
          // the picker exists to end.
          ...(reviewer === null
            ? {}
            : { model: reviewer.model, prompt_version: reviewer.promptVersion }),
        },
      }),
    // Switching windows keeps the previous rows on screen while the new ones load, the same
    // reason the KPI windows above do it.
    placeholderData: keepPreviousData,
  });
}

/** Filters the AI-review disagreement list takes. */
export interface AiReviewDisagreementsFilters {
  /** The same window {@link useAiReviewAgreement} is showing — the two must describe one span. */
  range: MetricsRange;
  /** Zero-based page. */
  page: number;
  /** Rows per page. */
  limit: number;
}

/**
 * The cases where the shadow reviewer and the operator reached different conclusions — the
 * artefact the whole feature exists for (an aggregate rate says how often they differ; only this
 * list says why).
 *
 * A second, independent query from {@link useAiReviewAgreement}, in the same shape
 * `useAmountDistribution` is independent of `useDashboardMetrics`: this list keeps rendering, or
 * keeps failing on its own, regardless of whether the agreement table above it loaded.
 */
export function useAiReviewDisagreements(
  filters: AiReviewDisagreementsFilters,
): UseQueryResult<AiReviewDisagreementsResponse> {
  const { range, page, limit } = filters;
  return useQuery({
    queryKey: queryKeys.ops.aiReviewDisagreements(range, page, limit),
    queryFn: ({ signal }) =>
      opsRequest(AiReviewDisagreementsResponseSchema, '/ai-review/disagreements', {
        signal,
        query: {
          ...(range.kind === 'preset'
            ? { window_hours: range.hours }
            : { from: range.from, to: range.to }),
          limit: String(limit),
          offset: String(page * limit),
        },
      }),
    // So paging feels like re-ordering rather than reloading — same reason `useDepositorAbandonment`
    // holds the previous page while the next one loads.
    placeholderData: keepPreviousData,
  });
}
